// ============================================
// RIDER WEB SOCKET SERVICE
// Real-time communication with backend
// ============================================

import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './auth';
import cookie from 'js-cookie';

interface SocketEventHandlers {
  'notification': (notification: any) => void;
  'notification:new': (notification: any) => void;
  'notification:update': (notification: any) => void;
  'incentive:updated': (incentive: any) => void;
  'wallet:updated': (balance: any) => void;
  'wallet:update': (data: any) => void;
  'payout:completed': (data: any) => void;
  'rider:status_changed': (status: string) => void;
  'rider:status': (data: any) => void;
  'emergency:response': (data: any) => void;
  'emergency:alert': (data: any) => void;
  'order:new': (order: any) => void;
  'order:assigned': (data: any) => void;
  'order:cancelled': (data: any) => void;
  'order:updated': (data: any) => void;
  'delivery:completed': (data: any) => void;
  'payment:completed': (data: any) => void;
  'surge:activated': (data: any) => void;
  'surge:updated': (data: any) => void;
  'earnings:updated': (data: any) => void;
  'geofence:entered': (data: any) => void;
  'location:update_required': () => void;
  connect: () => void;
  disconnect: () => void;
  connect_error: (error: Error) => void;
}

class SocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<keyof SocketEventHandlers, Function[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.setupSocket();
  }

  private setupSocket(): void {
    const token = cookie.get("token");

    const baseURL = `/riders/ws?token=${token}`;

    if (!token) {
      console.warn('No auth token available for socket connection');
      return;
    }

    // this.socket = io(baseURL, {
    //   reconnection: true,
    //   reconnectionAttempts: this.maxReconnectAttempts,
    //   reconnectionDelay: 1000,
    //   timeout: 20000,
    // });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id);
      this.reconnectAttempts = 0;
      this.emit('connect');
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('❌ Socket disconnected:', reason);
      this.emit('disconnect');
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      this.emit('connect_error', error);
      this.handleReconnect();
    });


    // Notification events
    this.socket.on('notification', (notification: any) => {
      console.log('🔔 Notification received:', notification);
      this.emit('notification', notification);
    });

    this.socket.on('notification:new', (notification: any) => {
      console.log('🔔 New notification:', notification);
      this.emit('notification:new', notification);
    });

    this.socket.on('notification:update', (notification: any) => {
      console.log('🔔 Notification updated:', notification);
      this.emit('notification:update', notification);
    });

    // Incentive events
    this.socket.on('incentive:updated', (incentive: any) => {
      console.log('💰 Incentive updated:', incentive);
      this.emit('incentive:updated', incentive);
    });

    // Wallet events
    this.socket.on('wallet:updated', (balance: any) => {
      console.log('💳 Wallet updated:', balance);
      this.emit('wallet:updated', balance);
    });

    this.socket.on('wallet:update', (data: any) => {
      console.log('💳 Wallet update:', data);
      this.emit('wallet:update', data);
    });

    this.socket.on('payout:completed', (data: any) => {
      console.log('💰 Payout completed:', data);
      this.emit('payout:completed', data);
    });

    // Order events
    this.socket.on('order:new', (order: any) => {
      console.log('📦 New order available:', order);
      this.emit('order:new', order);
    });

    this.socket.on('order:assigned', (data: any) => {
      console.log('📦 Order assigned:', data);
      this.emit('order:assigned', data);
    });

    this.socket.on('order:cancelled', (data: any) => {
      console.log('📦 Order cancelled:', data);
      this.emit('order:cancelled', data);
    });

    this.socket.on('order:updated', (data: any) => {
      console.log('📦 Order updated:', data);
      this.emit('order:updated', data);
    });

    // Delivery events
    this.socket.on('delivery:completed', (data: any) => {
      console.log('✅ Delivery completed:', data);
      this.emit('delivery:completed', data);
    });

    // Payment events
    this.socket.on('payment:completed', (data: any) => {
      console.log('💰 Payment completed:', data);
      this.emit('payment:completed', data);
    });

    // Surge events
    this.socket.on('surge:activated', (data: any) => {
      console.log('🔥 Surge activated:', data);
      this.emit('surge:activated', data);
    });

    this.socket.on('surge:updated', (data: any) => {
      console.log('🔥 Surge updated:', data);
      this.emit('surge:updated', data);
    });

    // Earnings events
    this.socket.on('earnings:updated', (data: any) => {
      console.log('💰 Earnings updated:', data);
      this.emit('earnings:updated', data);
    });

    // Status events
    this.socket.on('rider:status_changed', (status: string) => {
      console.log('👤 Status changed:', status);
      this.emit('rider:status_changed', status);
    });

    this.socket.on('rider:status', (data: any) => {
      console.log('👤 Rider status:', data);
      this.emit('rider:status', data);
    });

    // Emergency events
    this.socket.on('emergency:response', (data: any) => {
      console.log('🚨 Emergency response:', data);
      this.emit('emergency:response', data);
    });

    this.socket.on('emergency:alert', (data: any) => {
      console.log('🚨 Emergency alert:', data);
      this.emit('emergency:alert', data);
    });

    // Location events
    this.socket.on('location:update_required', () => {
      console.log('📍 Location update required');
      this.emit('location:update_required');
    });

    // Geofence events
    this.socket.on('geofence:entered', (data: any) => {
      console.log('📍 Geofence entered:', data);
      this.emit('geofence:entered', data);
    });
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff

    this.reconnectTimeout = setTimeout(() => {
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.setupSocket();
    }, delay);
  }

  // Event handling
  public on<K extends keyof SocketEventHandlers>(
    event: K,
    handler: SocketEventHandlers[K]
  ): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)?.push(handler);
  }

  public off<K extends keyof SocketEventHandlers>(
    event: K,
    handler?: SocketEventHandlers[K]
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      if (handler) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      } else {
        // Remove all handlers for this event
        this.eventHandlers.set(event, []);
      }
    }
  }


  // Socket methods
  public connect(): void {
    if (!this.socket) {
      this.setupSocket();
    } else if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  public disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Emit events to server
  public emitToServer(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit:', event);
    }
  }

  // Public emit method for internal use
  public emit<K extends keyof SocketEventHandlers>(
    event: K,
    ...args: Parameters<SocketEventHandlers[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`Error in socket event handler for ${event}:`, error);
        }
      });
    }
  }


  // Status updates
  public updateStatus(status: 'online' | 'offline' | 'busy'): void {
    this.emitToServer('rider:status', { status });
  }

  // Location updates
  public updateLocation(latitude: number, longitude: number): void {
    const rider = useAuthStore.getState().rider;
    if (rider?.id) {
      this.emitToServer('rider-location-update', {
        riderId: rider.id,
        latitude,
        longitude,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Room management
  public joinRiderRoom(riderId: string): void {
    this.emitToServer('join-rider-room', riderId);
  }

  public joinUserRoom(userId: string): void {
    this.emitToServer('join-user-room', userId);
  }

  public joinOrderRoom(orderId: string): void {
    this.emitToServer('join-order-room', orderId);
  }

  public leaveOrderRoom(orderId: string): void {
    this.emitToServer('leave-order-room', orderId);
  }

  // Emergency
  public sendSOS(location: { latitude: number; longitude: number }, message?: string): void {
    this.emitToServer('sos:trigger', { location, message });
  }

  // Heartbeat to keep connection alive
  public startHeartbeat(): void {
    if (this.socket) {
      setInterval(() => {
        if (this.socket?.connected) {
          this.socket.emit('ping');
        }
      }, 30000); // Every 30 seconds
    }
  }
}

// Create singleton instance
export const socketService = new SocketService();

// Hook for easy integration in React components
export function useSocket() {
  return {
    socket: socketService,
    isConnected: socketService.isConnected(),
    connect: () => socketService.connect(),
    disconnect: () => socketService.disconnect(),
    on: socketService.on.bind(socketService),
    off: socketService.off.bind(socketService),
    emit: socketService.emitToServer.bind(socketService),
    updateLocation: socketService.updateLocation.bind(socketService),
    updateStatus: socketService.updateStatus.bind(socketService),
    joinRiderRoom: socketService.joinRiderRoom.bind(socketService),
    joinUserRoom: socketService.joinUserRoom.bind(socketService),
    joinOrderRoom: socketService.joinOrderRoom.bind(socketService),
    leaveOrderRoom: socketService.leaveOrderRoom.bind(socketService),
    sendSOS: socketService.sendSOS.bind(socketService),
  };
}

export default socketService;